import React from 'react';
import {
  CheckSquareOutlined,
  EditOutlined,
  FileTextOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { fetchFiles } from '../../api/files/client';
import type { ClaimFile } from '../../api/files/interface';
import type { Task } from '../../api/tasks/interface';
import { CHIP_ICONS } from './chipIcons';
import styles from './MentionInput.module.scss';

export type MentionType = 'file' | 'task';

const CHIP_CLASS: Record<MentionType, string> = {
  file: styles.chipFile,
  task: styles.chipTask,
};

export interface MentionInputHandle {
  insertMentionById: (type: MentionType, id: string, label: string) => void;
  getValue: () => string;
  clear: () => void;
  focus: () => void;
  setText: (text: string) => void;
}

interface Props {
  placeholder?: string;
  disabled?: boolean;
  onChange?: (serialized: string) => void;
  onSend?: () => void;
  onChipClick?: (type: MentionType, id: string) => void;
  onCommand?: (command: 'upload' | 'note') => void;
  dossierId: string;
  tasks: Task[];
}

function createChip(
  type: MentionType,
  id: string,
  label: string
): HTMLSpanElement {
  const chip = document.createElement('span');
  chip.contentEditable = 'false';
  chip.dataset.mention = 'true';
  chip.dataset.mentionType = type;
  chip.dataset.mentionId = id;
  chip.className = `${styles.chip} ${CHIP_CLASS[type]}`;

  const icon = document.createElement('span');
  icon.className = styles.chipIcon;
  icon.innerHTML = CHIP_ICONS[type];
  chip.appendChild(icon);

  const lbl = document.createElement('span');
  lbl.className = styles.chipLabel;
  lbl.textContent = label;
  chip.appendChild(lbl);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.tabIndex = -1;
  btn.className = styles.chipRemove;
  btn.textContent = '×';
  chip.appendChild(btn);

  return chip;
}

function serializeEditor(el: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? '')
        .replace(/\u200B/g, '')
        .replace(/\u00A0/g, ' ');
    } else if (node instanceof HTMLElement) {
      if (node.dataset.mention === 'true') {
        const { mentionType: t, mentionId: id } = node.dataset;
        if (t === 'file') {
          out += `@file_id:${id}`;
        } else if (t === 'task') {
          out += `@task_id:${id}`;
        }
      } else {
        node.childNodes.forEach(walk);
      }
    }
  };
  el.childNodes.forEach(walk);
  return out;
}

function moveCursorAfter(node: Node) {
  const sel = window.getSelection();
  if (!sel) {
    return;
  }
  const r = document.createRange();
  r.setStartAfter(node);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

const COMMANDS: { id: 'upload' | 'note' | 'task'; label: string }[] = [
  { id: 'task', label: 'Create task' },
  { id: 'upload', label: 'Upload file' },
  { id: 'note', label: 'Add note' },
];

const MentionInput = React.forwardRef<MentionInputHandle, Props>(
  (
    {
      placeholder,
      disabled,
      onChange,
      onSend,
      onChipClick,
      onCommand,
      dossierId,
      tasks,
    },
    ref
  ) => {
    const editorRef = React.useRef<HTMLDivElement>(null);
    const savedRangeRef = React.useRef<Range | null>(null);
    const savedCmdRangeRef = React.useRef<Range | null>(null);

    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [activeIdx, setActiveIdx] = React.useState(0);
    const [files, setFiles] = React.useState<ClaimFile[]>([]);

    const [cmdOpen, setCmdOpen] = React.useState(false);
    const [cmdQuery, setCmdQuery] = React.useState('');
    const [cmdActiveIdx, setCmdActiveIdx] = React.useState(0);

    const activeItemRef = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
      activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIdx, cmdActiveIdx, open, cmdOpen]);

    React.useEffect(() => {
      if (!open) {
        return;
      }
      fetchFiles({ dossier_id: dossierId, stateDb: true })
        .then((result) => setFiles(result.files))
        .catch(() => {});
    }, [open, dossierId]);

    const notifyChange = React.useCallback(() => {
      const el = editorRef.current;
      if (!el) {
        return;
      }
      onChange?.(serializeEditor(el));
    }, [onChange]);

    React.useImperativeHandle(
      ref,
      () => ({
        insertMentionById(type, id, label) {
          const el = editorRef.current;
          if (!el) {
            return;
          }
          el.focus();
          const chip = createChip(type, id, label);
          const space = document.createTextNode('\u00A0');
          const sel = window.getSelection();
          if (
            sel &&
            sel.rangeCount > 0 &&
            el.contains(sel.getRangeAt(0).commonAncestorContainer)
          ) {
            const r = sel.getRangeAt(0);
            r.collapse(false);
            r.insertNode(space);
            r.insertNode(chip);
          } else {
            el.appendChild(chip);
            el.appendChild(space);
          }
          moveCursorAfter(space);
          notifyChange();
        },
        getValue: () => serializeEditor(editorRef.current!),
        clear() {
          if (editorRef.current) {
            editorRef.current.innerHTML = '';
          }
          onChange?.('');
        },
        focus: () => editorRef.current?.focus(),
        setText(text) {
          const el = editorRef.current;
          if (!el) {
            return;
          }
          el.textContent = text;
          notifyChange();
          el.focus();
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        },
      }),
      [notifyChange, onChange]
    );

    const detectMention = React.useCallback(() => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        setOpen(false);
        return;
      }
      const range = sel.getRangeAt(0);
      if (range.startContainer.nodeType !== Node.TEXT_NODE) {
        setOpen(false);
        return;
      }
      const before = (range.startContainer.textContent ?? '').slice(
        0,
        range.startOffset
      );
      const m = before.match(/@([^@\s]*)$/);
      if (m) {
        savedRangeRef.current = range.cloneRange();
        setQuery(m[1]);
        setActiveIdx(0);
        setOpen(true);
        setCmdOpen(false);
      } else {
        setOpen(false);
      }
    }, []);

    const detectCommand = React.useCallback(() => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        setCmdOpen(false);
        return;
      }
      const range = sel.getRangeAt(0);
      if (range.startContainer.nodeType !== Node.TEXT_NODE) {
        setCmdOpen(false);
        return;
      }
      const before = (range.startContainer.textContent ?? '').slice(
        0,
        range.startOffset
      );
      const m = before.match(/(^|\s)\/([^/\s]*)$/);
      if (m) {
        savedCmdRangeRef.current = range.cloneRange();
        setCmdQuery(m[2]);
        setCmdActiveIdx(0);
        setCmdOpen(true);
        setOpen(false);
      } else {
        setCmdOpen(false);
      }
    }, []);

    const insertFromDropdown = React.useCallback(
      (type: MentionType, id: string, label: string) => {
        const saved = savedRangeRef.current;
        const el = editorRef.current;
        if (!saved || !el) {
          return;
        }

        const textNode = saved.startContainer as Text;
        const text = textNode.textContent ?? '';
        const atIdx = text.lastIndexOf('@', saved.startOffset - 1);
        if (atIdx === -1) {
          return;
        }

        const del = document.createRange();
        del.setStart(textNode, atIdx);
        del.setEnd(textNode, saved.startOffset);
        del.deleteContents();

        const chip = createChip(type, id, label);
        const space = document.createTextNode('\u00A0');
        del.insertNode(space);
        del.insertNode(chip);
        moveCursorAfter(space);

        setOpen(false);
        notifyChange();
        el.focus();
      },
      [notifyChange]
    );

    const insertCommand = React.useCallback(
      (id: 'upload' | 'note' | 'task') => {
        const saved = savedCmdRangeRef.current;
        const el = editorRef.current;
        if (!saved || !el) {
          return;
        }

        const textNode = saved.startContainer as Text;
        const text = textNode.textContent ?? '';
        const slashIdx = text.lastIndexOf('/', saved.startOffset - 1);
        // No trigger `/` in the saved range — bail rather than insert at a
        // stale/document-start position (mirrors the `@` mention path).
        if (slashIdx === -1) {
          setCmdOpen(false);
          return;
        }
        const del = document.createRange();
        del.setStart(textNode, slashIdx);
        del.setEnd(textNode, saved.startOffset);
        del.deleteContents();

        // `task` is an inline-text command (`/task <title> …`): seed the
        // composer with `/task ` and let the user type the rest, parsed on
        // send. `upload`/`note` switch the composer into a dedicated mode.
        if (id === 'task') {
          const node = document.createTextNode('/task ');
          del.insertNode(node);
          moveCursorAfter(node);
          setCmdOpen(false);
          notifyChange();
          el.focus();
          return;
        }

        setCmdOpen(false);
        notifyChange();
        el.focus();
        onCommand?.(id);
      },
      [notifyChange, onCommand]
    );

    const filteredFiles = React.useMemo(() => {
      const q = query.toLowerCase();
      return files.filter(
        (f) =>
          f.file_type !== 'ACTIVITY' &&
          (!q || f.file_name.toLowerCase().includes(q))
      );
    }, [files, query]);
    const filteredTasks = React.useMemo(() => {
      const q = query.toLowerCase();
      return tasks.filter((t) => !q || t.title.toLowerCase().includes(q));
    }, [tasks, query]);
    const allOptions = React.useMemo(
      () => [
        ...filteredTasks.map((t) => ({
          type: 'task' as MentionType,
          id: t.id,
          label: t.title,
        })),
        ...filteredFiles.map((f) => ({
          type: 'file' as MentionType,
          id: f.file_id,
          label: f.file_name,
        })),
      ],
      [filteredTasks, filteredFiles]
    );

    const filteredCmds = React.useMemo(
      () =>
        COMMANDS.filter(
          (c) =>
            !cmdQuery || c.label.toLowerCase().includes(cmdQuery.toLowerCase())
        ),
      [cmdQuery]
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (cmdOpen) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCmdActiveIdx((i) => Math.min(i + 1, filteredCmds.length - 1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCmdActiveIdx((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const cmd = filteredCmds[cmdActiveIdx];
            if (cmd) {
              insertCommand(cmd.id);
            }
            return;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setCmdOpen(false);
            return;
          }
        }
        if (open) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, allOptions.length - 1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const opt = allOptions[activeIdx];
            if (opt) {
              insertFromDropdown(opt.type, opt.id, opt.label);
            }
            return;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            return;
          }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSend?.();
        }
      },
      [
        cmdOpen,
        filteredCmds,
        cmdActiveIdx,
        insertCommand,
        open,
        allOptions,
        activeIdx,
        insertFromDropdown,
        onSend,
      ]
    );

    const handleInput = React.useCallback(() => {
      notifyChange();
      detectMention();
      detectCommand();
    }, [notifyChange, detectMention, detectCommand]);

    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains(styles.chipRemove)) {
          const chip = target.closest('[data-mention]') as HTMLElement | null;
          chip?.remove();
          notifyChange();
          editorRef.current?.focus();
          return;
        }
        const chip = target.closest('[data-mention]') as HTMLElement | null;
        if (chip && onChipClick) {
          const { mentionType, mentionId } = chip.dataset;
          if (mentionType && mentionId) {
            onChipClick(mentionType as MentionType, mentionId);
          }
        }
      },
      [notifyChange, onChipClick]
    );

    const handlePaste = React.useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        notifyChange();
      },
      [notifyChange]
    );

    const hasOptions = allOptions.length > 0;
    const hasCmds = filteredCmds.length > 0;

    return (
      <div className={styles.wrapper}>
        {cmdOpen && hasCmds && (
          <div className={styles.dropdown}>
            <div className={styles.dropdownSection}>Actions</div>
            {filteredCmds.map((cmd, i) => (
              <button
                key={cmd.id}
                type="button"
                ref={i === cmdActiveIdx ? activeItemRef : undefined}
                className={`${styles.dropdownItem} ${
                  i === cmdActiveIdx ? styles.dropdownItemActive : ''
                }`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  insertCommand(cmd.id);
                }}
              >
                {cmd.id === 'task' ? (
                  <CheckSquareOutlined className={styles.dropdownItemIcon} />
                ) : cmd.id === 'upload' ? (
                  <PaperClipOutlined className={styles.dropdownItemIcon} />
                ) : (
                  <EditOutlined className={styles.dropdownItemIcon} />
                )}
                <span className={styles.itemLabel}>{cmd.label}</span>
              </button>
            ))}
          </div>
        )}
        {open && hasOptions && (
          <div className={styles.dropdown}>
            {filteredTasks.length > 0 && (
              <>
                <div className={styles.dropdownSection}>Tasks</div>
                {filteredTasks.map((t, i) => {
                  const optIdx = i;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      ref={optIdx === activeIdx ? activeItemRef : undefined}
                      className={`${styles.dropdownItem} ${
                        optIdx === activeIdx ? styles.dropdownItemActive : ''
                      }`}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        insertFromDropdown('task', t.id, t.title);
                      }}
                    >
                      <CheckSquareOutlined
                        className={styles.dropdownItemIcon}
                      />
                      <span className={styles.itemLabel}>{t.title}</span>
                    </button>
                  );
                })}
              </>
            )}
            {filteredFiles.length > 0 && (
              <>
                <div className={styles.dropdownSection}>Files</div>
                {filteredFiles.map((f, i) => {
                  const optIdx = filteredTasks.length + i;
                  return (
                    <button
                      key={f.file_id}
                      type="button"
                      ref={optIdx === activeIdx ? activeItemRef : undefined}
                      className={`${styles.dropdownItem} ${
                        optIdx === activeIdx ? styles.dropdownItemActive : ''
                      }`}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        insertFromDropdown('file', f.file_id, f.file_name);
                      }}
                    >
                      <FileTextOutlined className={styles.dropdownItemIcon} />
                      <span className={styles.itemLabel}>{f.file_name}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          className={styles.editor}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          onPaste={handlePaste}
        />
      </div>
    );
  }
);

MentionInput.displayName = 'MentionInput';
export default MentionInput;
